import React, { useState, useEffect } from 'react';
import { ArrowDown, Rocket, TrendingUp, Users, Wrench } from 'lucide-react';
import { Widget } from '@typeform/embed-react';
import CustomCursor from '../components/CustomCursor';
import Seo from '../components/Seo';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useLanguage } from '../context/LanguageContext';
import './LandingPage.css';      // reuse the site design system (nav, footer, sections, cards, buttons)
import './IncubatorPage.css';    // page-specific layout

// "What you get" cards — icon + i18n key. Copy lives in translations.js (incubator.get.cN).
const GET_CARDS = [
  { icon: Rocket, key: 'c1' },
  { icon: TrendingUp, key: 'c2' },
  { icon: Users, key: 'c3' },
  { icon: Wrench, key: 'c4' },
];

const IncubatorPage = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(new Set());

  // Scroll-reveal, same approach/feel as the landing page.
  useEffect(() => {
    const onScroll = () => {
      document.querySelectorAll('.animate-on-scroll').forEach((s) => {
        if (s.getBoundingClientRect().top < window.innerHeight * 0.85) {
          setVisible((prev) => new Set(prev).add(s.id));
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const vis = (id) => (visible.has(id) ? 'visible' : '');

  const scrollToApply = () => {
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="incubator-page">
      <Seo titleKey="seo.incubator.title" descKey="seo.incubator.description" path="/incubator" />
      <CustomCursor />
      <SiteNav />

      {/* ============================================================
          SECTION 1 — HERO
         ============================================================ */}
      <section className="incubator-hero" id="hero">
        <div className="incubator-hero-inner">
          <h1 className="visually-hidden">{t('incubator.hero.h1')}</h1>
          <img className="incubator-hero-logo" src="/14_horizontal_transparent_nopadding.png" alt="BCG Incubator" />
          <p className="incubator-hero-sub">{t('incubator.hero.sub')}</p>
          <button className="btn-primary" onClick={scrollToApply}>
            {t('incubator.hero.cta')}
            <ArrowDown size={18} />
          </button>
        </div>
      </section>

      {/* ============================================================
          SECTION 3 — WHAT YOU GET (4 cards)
         ============================================================ */}
      <section className="section incubator-section animate-on-scroll" id="get">
        <div className={`section-content ${vis('get')}`}>
          <div className="section-label">{t('incubator.get.eyebrow')}</div>
          <h2 className="section-title">{t('incubator.get.heading')}</h2>
          <div className="incubator-get-grid">
            {GET_CARDS.map(({ icon: Icon, key }) => (
              <div className="venture-card" key={key}>
                <div className="venture-icon">
                  <Icon size={40} strokeWidth={1.5} />
                </div>
                <div className="venture-card-content">
                  <h3>{t(`incubator.get.${key}.t`)}</h3>
                  <p>{t(`incubator.get.${key}.d`)}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="incubator-get-note">{t('incubator.get.note')}</p>
        </div>
      </section>

      {/* ============================================================
          SECTION 6 — APPLY (Typeform embed placeholder)
         ============================================================ */}
      <section className="section incubator-section-alt animate-on-scroll" id="apply">
        <div className={`section-content ${vis('apply')}`}>
          <div className="section-label">{t('incubator.apply.eyebrow')}</div>
          <h2 className="section-title">{t('incubator.apply.heading')} <span className="incubator-accent">Incubator</span></h2>

          <div className="typeform-embed">
            <Widget
              id="lW55yTVo"
              className="typeform-widget"
              autoFocus={false}
              inlineOnMobile
              disableScroll
            />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default IncubatorPage;
